import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ToolsPage.css';
import NavigationButtons from '../components/NavigationButtons';
import PokoViewer from '../components/PokoViewer';
import poko from '../assets/poko_handsInWaist.png';

// Local dictionary lookup (replaces the old /api/check-word/ backend call).
// The word list ships as a static asset and is fetched once on first use,
// then cached in memory for the rest of the session.
let dictionaryPromise = null;

const loadDictionary = () => {
	if (!dictionaryPromise) {
		dictionaryPromise = fetch(`${process.env.PUBLIC_URL}/data/words.txt`)
			.then((res) => {
				if (!res.ok) throw new Error(`Failed to load dictionary: ${res.status}`);
				return res.text();
			})
			.then((text) => new Set(text.split('\n')))
			.catch((err) => {
				dictionaryPromise = null; // allow retry on the next validation
				throw err;
			});
	}
	return dictionaryPromise;
};

/*
leave for iteration 3
'🌙 Good Night',
'💗 Sorry',
'❤️ Love',
'✅ Yes',
'🙅‍♂️ No',
'☹️ Bad',
*/
const words = [
	// 👋 Greetings
	'🙌 Hello',
	'👋 Goodbye',
	'🙋‍♂️ How are you',
	
  
	// 🙏 Manners & Politeness
	'🙏 Thanks',
	'😊 Please',
  
	// 💬 Feelings & Emotions
	'😄 Happy',
	'😢 Sad',
	'☺️ Smile',
	'🥰 Like',
  
	// 💕 Relationships
	'🤟 I love you',
  
	// 👥 People & Pronouns
	'🧍 Me',
	'👤 You',
	'👩‍🏫 Teacher',
  
	// 👍 Responses & Actions
	'👍 Good',
	'🍀 Good Luck',
	'🛑 Stop',
	'👉 Come',
  
	// 🛏️ Time & Routine
	'🛏️ Bed',
	'👀 See',
	'🤝 Have'
  ];
  

// Map of common words to their single-action animations
const commonWordAnimations = {
	'Hello': 'Word Hello',
	'Goodbye': 'Word Bye',
	'How are you': 'Word How are you',
	'Good Night': 'Word Good Night',
  
	'Thanks': 'Word Thanks',
	'Please': 'Word Please',
	'Sorry': 'Word Sorry',
  
	'Happy': 'Word Happy',
	'Sad': 'Word Sad',
	'Smile': 'Word Smile',
	'Like': 'Word Like',
	'Bad': 'Word Bad',
  
	'Love': 'Word Love',
	'I love you': 'Word Love',
  
	'Me': 'Word Me',
	'You': 'Word You',
	'Teacher': 'Word Teacher',
  
	'Good': 'Word Good',
	'Good Luck': 'Word Good Luck',
	'Yes': 'Word Yes',
	'No': 'Word No',
	'Stop': 'Word Stop',
	'Come': 'Word Come',
  
	'Bed': 'Word Bed',
	'See': 'Word See',
	'Have': 'Word Have'
  };
  

// Helper function to check if a word has a single-action animation
const hasCommonWordAnimation = (word) => {
	// Extract the actual word part (after the emoji and space)
	const actualWord = word.split(' ').slice(1).join(' ');
	return !!commonWordAnimations[actualWord];
};

// Helper function to get the animation for a common word
const getCommonWordAnimation = (word) => {
	const actualWord = word.split(' ').slice(1).join(' ');
	return commonWordAnimations[actualWord];
};

// Numbers 0-10 and Letters A-Z are available in the GLB file
const availableNumbers = Array.from({ length: 11 }, (_, i) => i.toString()); // 0-10
const availableLetters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)); // A-Z

function ToolsPage() {
	const navigate = useNavigate();
	const [selectedWord, setSelectedWord] = useState('');
	const [currentCharIndex, setCurrentCharIndex] = useState(0);
	const [useMyWord, setUseMyWord] = useState(false);
	const [customWord, setCustomWord] = useState('');
	const [isPlayingSequence, setIsPlayingSequence] = useState(false);
	const [sequenceTimer, setSequenceTimer] = useState(null);
	const [sequenceIndex, setSequenceIndex] = useState(0);
	const [validationError, setValidationError] = useState('');
	const [isModelLoading, setIsModelLoading] = useState(true);
	const [isValidating, setIsValidating] = useState(false);
	const [showInstructions, setShowInstructions] = useState(false);
	const [sequenceCompleted, setSequenceCompleted] = useState(false);
	const [isListening, setIsListening] = useState(false);
	const [speechError, setSpeechError] = useState('');
	const recognitionRef = useRef(null);

	// State for the 3D model configuration
	const [modelConfig, setModelConfig] = useState({
		scale: 2.2,
		animationSpeed: 0.6,
		currentAnimation: 'Letter dummy', // Default pose
		isPlayingSequence: false,
		onModelLoaded: () => setIsModelLoading(false)
	});

	// Ref to track previous character and index for animationKey logic
	const prevCharRef = useRef({ index: -1, char: '' });

	// Add a ref to track sequence state
	const sequenceStateRef = useRef({
		isPlaying: false,
		isCompleted: false,
		lastWord: null,
		shouldAutoPlay: true
	});

	// Get the current character to display
	const getCurrentChar = () => {
		if (!selectedWord) return '';
		// Get the part after the emoji prefix, and remove spaces
		const word = selectedWord.split(' ').slice(1).join('').replace(/\s/g, '');
		return word[currentCharIndex] || '';
	};

	// Check if there is a selected word to sign
	const hasSelectedWord = () => {
		return !!selectedWord && selectedWord.split(' ').slice(1).join('').length > 0;
	};

	// Determine if the current character has a valid animation
	const hasAnimation = (char) => {
		// Check if it's a number (0-10)
		if (availableNumbers.includes(char)) {
			return true;
		}
		// Check if it's a letter (A-Z)
		if (availableLetters.includes(char.toUpperCase())) {
			return true;
		}
		return false;
	};

	// Get animation for a specific character
	const getAnimationForChar = (char, isFirstInSequence) => {
		if (!char) return 'Letter dummy';

		// Check if it's a number
		if (availableNumbers.includes(char)) {
			return `Number ${char}`;
		}

		// Check if it's a letter
		if (availableLetters.includes(char.toUpperCase())) {
			return `Letter ${char.toUpperCase()}`;
		}

		// If it's the first character in sequence, use Number 0 as default pose
		// Otherwise return to default pose
		return isFirstInSequence ? 'Number 0' : 'Letter dummy';
	};

	// Show animation for individual character
	const showCharacterAnimation = (char, index) => {
		// Stop any running sequence
		if (isPlayingSequence) {
			if (sequenceTimer) {
				clearTimeout(sequenceTimer);
				setSequenceTimer(null);
			}
			setIsPlayingSequence(false);
		}

		// For common words, show the single action
		if (hasCommonWordAnimation(selectedWord)) {
			setCurrentCharIndex(index);
			setModelConfig(prev => ({
				...prev,
				currentAnimation: getCommonWordAnimation(selectedWord),
				isPlayingSequence: false
			}));
			return;
		}

		// Original letter-by-letter logic for other words
		setCurrentCharIndex(index);
		setModelConfig(prev => ({
			...prev,
			currentAnimation: getAnimationForChar(char, false),
			isPlayingSequence: false
		}));
	};

	// Update the model outside of sequence playback
	useEffect(() => {
		if (!isPlayingSequence) {
			if (hasSelectedWord()) {
				if (hasCommonWordAnimation(selectedWord)) {
					// For common words, only show the word animation or default pose
					const currentAnim = modelConfig.currentAnimation;
					if (currentAnim !== getCommonWordAnimation(selectedWord) && 
						currentAnim !== 'Letter dummy') {
						setModelConfig(prev => ({
							...prev,
							currentAnimation: 'Letter dummy',
							isPlayingSequence: false
						}));
					}
				} else {
					// For regular words, show letter animations
					const currentChar = getCurrentChar();
					setModelConfig(prev => ({
						...prev,
						currentAnimation: getAnimationForChar(currentChar, currentCharIndex === 0),
						isPlayingSequence: false
					}));
				}
			} else {
				// No word selected, show default pose
				setModelConfig(prev => ({
					...prev,
					currentAnimation: 'Letter dummy',
					isPlayingSequence: false
				}));
			}
		}
	}, [currentCharIndex, selectedWord, isPlayingSequence]);

	// Calculate animation duration based on character
	const getAnimationDuration = (char, isFirstInSequence) => {
		// Longer duration for the first character
		if (isFirstInSequence) {
			return 2400;
		}

		// Standard duration for numbers with animations
		if (hasAnimation(char)) {
			return 2400;
		}

		// Shorter duration for characters without specific animations
		return 2400;
	};

	// Validate word against the local dictionary
	const validateWordWithDictionary = async (word) => {
		try {
			setIsValidating(true);
			const dictionary = await loadDictionary();
			return dictionary.has(word.toLowerCase());
		} catch (error) {
			console.error('Error loading dictionary:', error);
			return false;
		} finally {
			setIsValidating(false);
		}
	};

	// Add a helper function to check if a word matches any common word (case-insensitive)
	const findMatchingCommonWord = (word) => {
		const normalizedInput = word.toLowerCase().trim();
		for (const [commonWord, animation] of Object.entries(commonWordAnimations)) {
			if (commonWord.toLowerCase() === normalizedInput) {
				return commonWord; // Return the properly cased common word
			}
		}
		return null;
	};

	// Modify the validateCustomWord function
	const validateCustomWord = async (word) => {
		// Skip all validation if we're using voice recognition
		if (isListening) {
			return true;
		}

		// Remove excess spaces and check if the word is empty
		const trimmedWord = word.trim();
		if (!trimmedWord) {
			setValidationError('Please type a word');
			return false;
		}

		// Check if the word has only alphabets, numbers, and spaces
		if (!/^[a-zA-Z0-9\s]+$/.test(trimmedWord)) {
			setValidationError('Only type letters, numbers, and spaces');
			return false;
		}

		// Check if the word is not too long (excluding spaces)
		if (trimmedWord.replace(/\s/g, '').length > 20) {
			setValidationError('Should be shorter than 20 characters');
			return false;
		}

		// First check if it matches any common word
		const matchingCommonWord = findMatchingCommonWord(trimmedWord);
		if (matchingCommonWord) {
			// It's a common word, no need to validate with API
			setValidationError('');
			return true;
		}

		// If not a common word, validate against the dictionary
		const isValidWord = await validateWordWithDictionary(trimmedWord);
		if (!isValidWord) {
			setValidationError('Word not found in dictionary');
			return false;
		}

		// Word is valid
		setValidationError('');
		return true;
	};

	// Modify the submitCustomWord function
	const submitCustomWord = async () => {
		// Skip validation if we're using voice recognition
		if (isListening) {
			const trimmedWord = customWord.trim();
			if (trimmedWord) {
				// Process the word directly without validation
				if (sequenceTimer) {
					clearTimeout(sequenceTimer);
					setSequenceTimer(null);
				}
				setIsPlayingSequence(false);
				setSequenceIndex(0);
				setCurrentCharIndex(0);

				// Check if it matches any common word first
				const matchingCommonWord = findMatchingCommonWord(trimmedWord);
				
				if (matchingCommonWord) {
					const matchingWordWithEmoji = words.find(w => w.split(' ').slice(1).join(' ') === matchingCommonWord);
					setSelectedWord(matchingWordWithEmoji || `✍️ ${matchingCommonWord}`);
				} else {
					const processedWord = trimmedWord.replace(/\s/g, '');
					setSelectedWord(`✍️ ${processedWord}`);
				}

				// Reset sequence state and start new sequence
				sequenceStateRef.current = {
					isPlaying: false,
					isCompleted: false,
					lastWord: null,
					shouldAutoPlay: true
				};

				setModelConfig(prev => ({
					...prev,
					currentAnimation: 'Letter dummy',
					isPlayingSequence: false,
					animationKey: Date.now() + '-reset'
				}));

				startSequence();
			}
			return;
		}

		// Normal validation flow for manual input
		if (await validateCustomWord(customWord)) {
			// Kill everything first
			if (sequenceTimer) {
				clearTimeout(sequenceTimer);
				setSequenceTimer(null);
			}
			setIsPlayingSequence(false);
			setSequenceIndex(0);
			setCurrentCharIndex(0);

			// Check if it matches any common word first
			const matchingCommonWord = findMatchingCommonWord(customWord);
			
			if (matchingCommonWord) {
				// If it's a common word, use the emoji from the words array that matches this word
				const matchingWordWithEmoji = words.find(w => w.split(' ').slice(1).join(' ') === matchingCommonWord);
				setSelectedWord(matchingWordWithEmoji || `✍️ ${matchingCommonWord}`);
			} else {
				// For non-common words, use the normal processing
				const trimmedWord = customWord.trim().replace(/\s{2,}/g, ' ');
				const processedWord = trimmedWord.replace(/\s/g, '');
				setSelectedWord(`✍️ ${processedWord}`);
			}

			// Nuke sequence state
			sequenceStateRef.current = {
				isPlaying: false,
				isCompleted: false,
				lastWord: null,
				shouldAutoPlay: true
			};

			// Force reset to default pose
			setModelConfig(prev => ({
				...prev,
				currentAnimation: 'Letter dummy',
				isPlayingSequence: false,
				animationKey: Date.now() + '-reset'
			}));

			// Start new sequence NOW
			startSequence();
		}
	};

	// Modify the playNextInSequence function
	const playNextInSequence = (index, word) => {
		// If we're already showing the dummy pose, don't continue
		if (index > word.length) {
			return;
		}

		setCurrentCharIndex(index);
		setSequenceIndex(index);

		// If we've finished all letters, show the default pose
		if (index === word.length) {
			// Set the dummy pose with a fresh animation key
			setModelConfig(prev => ({
				...prev,
				currentAnimation: 'Letter dummy',
				isPlayingSequence: false, // Set to false to prevent further progression
				animationKey: Date.now() + '-dummy' // Force a fresh animation
			}));

			// Clear any existing timer
			if (sequenceTimer) {
				clearTimeout(sequenceTimer);
				setSequenceTimer(null);
			}

			// Update sequence state but allow replay
			sequenceStateRef.current.isCompleted = true;
			sequenceStateRef.current.isPlaying = false;
			sequenceStateRef.current.shouldAutoPlay = true; // Allow replay
			
			// Update playing state to change button text
			setIsPlayingSequence(false);
			return;
		}

		const currentChar = word[index];
		const isFirstInSequence = index === 0;

		// Check if the current character is the same as the previous one
		const prevChar = prevCharRef.current.char;
		const prevIndex = prevCharRef.current.index;

		if (index > 0 && currentChar === prevChar) {
			// Force remount for repeated character
			setModelConfig(prev => ({
				...prev,
				currentAnimation: getAnimationForChar(currentChar, isFirstInSequence),
				isPlayingSequence: true,
				animationKey: Date.now() + '-' + index
			}));
		} else {
			// Normal update for unique or first character
			setModelConfig(prev => ({
				...prev,
				currentAnimation: getAnimationForChar(currentChar, isFirstInSequence),
				isPlayingSequence: true
			}));
		}

		// Update the ref for next call
		prevCharRef.current = { index, char: currentChar };

		const duration = getAnimationDuration(currentChar, isFirstInSequence);
		const nextTimer = setTimeout(() => {
			playNextInSequence(index + 1, word);
		}, duration);

		setSequenceTimer(nextTimer);
	};

	// Ultra-aggressive handleWordSelection
	const handleWordSelection = (word) => {
		// Kill everything immediately
		if (sequenceTimer) {
			clearTimeout(sequenceTimer);
			setSequenceTimer(null);
		}
		setIsPlayingSequence(false);
		setSequenceIndex(0);
		setCurrentCharIndex(0);
		
		// Nuke all state
		sequenceStateRef.current = {
			isPlaying: false,
			isCompleted: false,
			lastWord: word,
			shouldAutoPlay: true
		};

		// Force new word
		setSelectedWord(word);
		setUseMyWord(false);

		// Force default pose
		setModelConfig(prev => ({
			...prev,
			currentAnimation: 'Letter dummy',
			isPlayingSequence: false,
			animationKey: Date.now() + '-reset'
		}));

		// Start new sequence NOW
		startSequence();
	};

	// Ultra-aggressive auto-play
	useEffect(() => {
		if (selectedWord && sequenceStateRef.current.lastWord !== selectedWord) {
			// Kill everything
			if (sequenceTimer) {
				clearTimeout(sequenceTimer);
				setSequenceTimer(null);
			}
			setIsPlayingSequence(false);
			setSequenceIndex(0);
			setCurrentCharIndex(0);

			// Nuke state
			sequenceStateRef.current = {
				isPlaying: false,
				isCompleted: false,
				lastWord: selectedWord,
				shouldAutoPlay: true
			};

			// Start new sequence NOW
			startSequence();
		}
	}, [selectedWord]);

	// Ultra-aggressive startSequence
	const startSequence = () => {
		// Nuke state
		sequenceStateRef.current = {
			isPlaying: true,
			isCompleted: false,
			lastWord: selectedWord,
			shouldAutoPlay: true
		};

		setIsPlayingSequence(true);
		setSequenceIndex(0);
		setCurrentCharIndex(0);

		// Common word
		if (hasCommonWordAnimation(selectedWord)) {
			setModelConfig(prev => ({
				...prev,
				currentAnimation: getCommonWordAnimation(selectedWord),
				isPlayingSequence: true,
				animationKey: Date.now()
			}));

			const timer = setTimeout(() => {
				setIsPlayingSequence(false);
				stopSequence();
			}, 2400);

			setSequenceTimer(timer);
			return;
		}

		// Letter sequence
		const wordPart = selectedWord.split(' ').slice(1).join('');
		const word = wordPart.replace(/\s/g, '');

		if (word.length === 0) return;

		playNextInSequence(0, word);
	};

	// Reset sequence completion when a new word is selected
	useEffect(() => {
		setSequenceCompleted(false);
	}, [selectedWord]);

	// Check if it's first visit
	useEffect(() => {
		const hasVisited = localStorage.getItem('hasVisitedToolsPage');
		if (!hasVisited) {
			setShowInstructions(true);
			localStorage.setItem('hasVisitedToolsPage', 'true');
		}
	}, []);

	// Instructions modal content
	const InstructionsModal = () => (
		<div className="instructions-modal-overlay" onClick={() => setShowInstructions(false)}>
			<div className="instructions-modal" onClick={e => e.stopPropagation()}>
				<button className="close-button" onClick={() => setShowInstructions(false)}>×</button>
				<h2>How to Use?</h2>

				<div className="instruction-section">
					<h3>1. Choose a Word</h3>
					<p>• Click on any word button, or type your own word!</p>
				</div>

				<div className="instruction-section">
					<h3>2. Watch Noko Signs</h3>
					<p>• Click individual letters to see their signs</p>
					<p>• Use the Play button to see the whole word signed</p>
				</div>

				<div className="instruction-section">
					<h3>3. Control 3D View</h3>
					<p>• Use ➕ and ➖ to zoom in/out</p>
					<p>• Use 👈🏻 and 👉🏻 to rotate the view</p>
					<p>• Click 🔍 for a close-up view</p>
					<p>• Click 🏠 to reset the view</p>
				</div>
			</div>
		</div>
	);

	// Add back the stopSequence function
	const stopSequence = () => {
		if (sequenceTimer) {
			clearTimeout(sequenceTimer);
			setSequenceTimer(null);
		}
		setIsPlayingSequence(false);
		setCurrentCharIndex(0);
		setSequenceIndex(0);

		// Return to the default pose
		setModelConfig(prev => ({
			...prev,
			currentAnimation: 'Letter dummy',
			isPlayingSequence: false,
			animationKey: Date.now() + '-stop' // Force a fresh default pose
		}));
	};

	// Initialize speech recognition
	useEffect(() => {
		if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
			const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
			recognitionRef.current = new SpeechRecognition();
			recognitionRef.current.continuous = false;
			recognitionRef.current.interimResults = false;
			recognitionRef.current.lang = 'en-US';

			recognitionRef.current.onresult = (event) => {
				const transcript = event.results[0][0].transcript.trim().toLowerCase();
				setIsListening(false);
				setValidationError(''); // Clear any validation error
				
				// First check if the entire transcript matches a common word
				const matchingCommonWord = findMatchingCommonWord(transcript);
				
				if (matchingCommonWord) {
					// If it's a common word, use it directly
					setCustomWord(matchingCommonWord);
					const matchingWordWithEmoji = words.find(w => w.split(' ').slice(1).join(' ') === matchingCommonWord);
					setSelectedWord(matchingWordWithEmoji || `✍️ ${matchingCommonWord}`);
				} else {
					// If not a common word, take only the first word
					const firstWord = transcript.split(' ')[0];
					
					// Check if the first word is a common word
					const firstWordMatch = findMatchingCommonWord(firstWord);
					
					if (firstWordMatch) {
						// If first word is a common word, use it
						setCustomWord(firstWordMatch);
						const matchingWordWithEmoji = words.find(w => w.split(' ').slice(1).join(' ') === firstWordMatch);
						setSelectedWord(matchingWordWithEmoji || `✍️ ${firstWordMatch}`);
					} else {
						// If first word is not a common word, use it as is
						setCustomWord(firstWord);
						setSelectedWord(`✍️ ${firstWord}`);
					}
				}

				// Process the selected word
				if (sequenceTimer) {
					clearTimeout(sequenceTimer);
					setSequenceTimer(null);
				}
				setIsPlayingSequence(false);
				setSequenceIndex(0);
				setCurrentCharIndex(0);

				// Reset sequence state and start new sequence
				sequenceStateRef.current = {
					isPlaying: false,
					isCompleted: false,
					lastWord: null,
					shouldAutoPlay: true
				};

				setModelConfig(prev => ({
					...prev,
					currentAnimation: 'Letter dummy',
					isPlayingSequence: false,
					animationKey: Date.now() + '-reset'
				}));

				startSequence();
			};

			recognitionRef.current.onerror = (event) => {
				setSpeechError('Error recognizing speech. Please try again.');
				setIsListening(false);
			};

			recognitionRef.current.onend = () => {
				setIsListening(false);
			};
		} else {
			setSpeechError('Speech recognition is not supported in your browser.');
		}

		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, []);

	const startListening = () => {
		setSpeechError('');
		setIsListening(true);
		if (recognitionRef.current) {
			recognitionRef.current.start();
		}
	};

	const stopListening = () => {
		setIsListening(false);
		if (recognitionRef.current) {
			recognitionRef.current.stop();
		}
	};

	// Modify handleCustomWordChange to prevent validation during voice recognition
	const handleCustomWordChange = (e) => {
		const value = e.target.value;
		const normalizedValue = value.replace(/\s{2,}/g, ' ');
		const charCount = normalizedValue.replace(/\s/g, '').length;

		setCustomWord(normalizedValue);

		// Only show validation errors if we're not using voice recognition
		if (!isListening) {
			if (!normalizedValue) {
				setValidationError('Please type a word');
			} else if (charCount > 20) {
				setValidationError('Should be shorter than 20 characters');
			} else if (validationError === 'Should be shorter than 20 characters') {
				setValidationError('');
			}
		}
	};

	return (
		<div className="tools-page page">
			<div className="white-wrapper container">
				<div className="page-title">
					<h1>🧰 Tools</h1>
					<h5>Explore common signs and fingerspell your own words!</h5>
					<button
						className="help-button"
						onClick={() => setShowInstructions(true)}
						title="Show Instructions"
					>
						❔
					</button>
				</div>
				<div className='content-wrapper'>
					<div className="speech-area">
						<img src={poko} alt="Poko character" className="poko" />
						<div className="speech-bubble-right">
							<p><strong>Hiya, Sign Explorer!</strong><br />
								Tab a word and Noko will show you the Auslan sign for it! 🤟✨</p>
							<div className="word-buttons">
								{words.map((word, idx) => (
									<button
										key={idx}
										className={`word-button ${selectedWord === word && !useMyWord ? 'active' : ''}`}
										onClick={() => handleWordSelection(word)}
									>
										{word}
									</button>
								))}

							</div>
							<div className='custom-word-wrapper'>
								<p className='custom-word-instruction'>
									Want to spell your own word? Try <strong>"Use my word"</strong> to see it fingerspelled, letter by letter! 🔤
								</p>
								<button
									className={`word-button ${useMyWord ? 'active' : ''}`}
									onClick={() => {
										setUseMyWord(true);
										setValidationError('');
									}}
								>
									✍️ Use my word
								</button>

								{useMyWord && (
									<div className="custom-word-input">
										<input
											type="text"
											placeholder="Type your word..."
											value={customWord}
											onChange={handleCustomWordChange}
											disabled={isValidating}
										/>
										<button
											className="submit-word"
											onClick={submitCustomWord}
											disabled={isValidating}
										>
											{isValidating ? 'Validating...' : 'Submit'}
										</button>
										<button
											className={`speak-button ${isListening ? 'listening' : ''}`}
											onClick={isListening ? stopListening : startListening}
											title={isListening ? 'Stop listening' : 'Speak your word'}
										>
											{isListening ? '👂' : '🎙️'}
										</button>
										{validationError && (
											<div className="validation-error">
												{validationError}
											</div>
										)}
										{speechError && (
											<div className="validation-error">
												{speechError}
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="model-area">
						{isModelLoading && (
							<div className="loading-indicator">
								<div className="loading-spinner"></div>
								<p>Loading 3D model...</p>
							</div>
						)}
						<div className="model-container">
							<PokoViewer config={modelConfig} />
						</div>
						<div className="model-controls">
							<div className="char-buttons">
								{(() => {
									if (!hasSelectedWord()) {
										return (
											<div className="empty-selection-message">
												👈 Tab a word to see its sign!
											</div>
										);
									}

									// For common words, show a single button
									if (hasCommonWordAnimation(selectedWord)) {
										const wordToDisplay = selectedWord.split(' ').slice(1).join(' ');
										return (
											<>
												<div className="char-selection-instruction">
													Click to see the sign for "{wordToDisplay}"
												</div>
												<div className="char-buttons-row">
													<button
														className="char-button active full-word"
														onClick={() => showCharacterAnimation(null, 0)}
													>
														{wordToDisplay}
													</button>
												</div>
											</>
										);
									}

									// Original letter-by-letter display for other words
									const wordToDisplay = selectedWord.startsWith('✍️')
										? selectedWord.split(' ').slice(1).join('').replace(/\s/g, '')
										: selectedWord.split(' ')[1];

									// Group characters into rows of 10 max
									const characters = [...wordToDisplay];
									const rows = [];
									for (let i = 0; i < characters.length; i += 10) {
										rows.push(characters.slice(i, i + 10));
									}

									return (
										<>
											<div className="char-selection-instruction">
												Click a letter to see its sign!
											</div>
											{rows.map((row, rowIndex) => (
												<div key={`row-${rowIndex}`} className="char-buttons-row">
													{row.map((char, charIndex) => {
														const idx = rowIndex * 10 + charIndex;
														return (
															<button
																key={idx}
																className={`char-button ${currentCharIndex === idx ? 'active' : ''}`}
																onClick={() => showCharacterAnimation(char, idx)}
															>
																{char}
															</button>
														);
													})}
												</div>
											))}
										</>
									);
								})()}
							</div>

							<button
								className={`sequence-button ${isPlayingSequence ? 'active' : ''} ${hasSelectedWord() ? 'show' : ''}`}
								onClick={startSequence}
							>
								{isPlayingSequence ? '◼︎ Stop' : '▶︎ Play'}
							</button>
						</div>
					</div>
				</div>
			</div>

			<NavigationButtons />
			{showInstructions && <InstructionsModal />}
		</div>
	);
}

export default ToolsPage;
